import { Component, ErrorInfo, ReactNode } from 'react';

import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

import { logger } from '@/utils/logger';

import { isChunkLoadError } from '../utils/chunkLoadError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Keep development diagnostics behind the centralized logger.
    if (import.meta.env.DEV) {
      logger.error('ErrorBoundary caught an error', error, { metadata: { componentStack: errorInfo.componentStack } });
    }

    // Update state with error details
    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // In production, you might want to log to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, render it
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Lazy route chunks land here when they fail to load; an in-place
      // "Try Again" can never succeed (React caches the rejected lazy and the
      // browser's module map re-rejects without refetching) — offer Reload.
      const isChunkError = isChunkLoadError(this.state.error);

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Oops! Something went wrong
            </h1>
            
            <p className="text-gray-600 mb-6">
              {isChunkError
                ? 'Part of the app failed to download. Check your connection and reload the page.'
                : 'We apologize for the inconvenience. The application encountered an unexpected error.'}
            </p>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-gray-100 rounded-lg text-left">
                <p className="text-sm font-mono text-gray-700 mb-2">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <details className="text-xs text-gray-600">
                    <summary className="cursor-pointer font-semibold mb-1">
                      Stack Trace
                    </summary>
                    <pre className="overflow-auto max-h-40 mt-2">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={isChunkError ? () => window.location.reload() : this.handleReset}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-brand-dark-green text-white rounded-lg hover:bg-brand-dark-green/90 transition-colors"
              >
                <RefreshCw size={16} />
                {isChunkError ? 'Reload' : 'Try Again'}
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <Home size={16} />
                Go to Homepage
              </button>
            </div>

            {this.state.errorCount > 2 && (
              <p className="mt-4 text-sm text-gray-500">
                If this problem persists, please contact support.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
